import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ErrorTryCatch from 'components/consumer/error-try-catch';
import classNames from 'classnames';
import MixedGridCard from './MixedGridCard';
import MixedGridCardPlaceholder from './MixedGridCardPlaceholder';

const MixedGridItemsWithReviews = ({
  loadingMore,
  data,
  hits,
  hoveredValue,
  layout,
  onHoverHandler,
  toggleModalView,
  isMixedGridTab,
  allStaticProductData = [],
}) => {
  const gridItems = useMemo(() => {
    const items = [];
    const reviewsList = [];
    const productsList = [];
    const photosList = [];

    data.forEach(each => {
      if (each.card_type === 'review') {
        reviewsList.push(each);
      } else if (each.card_type === 'product') {
        productsList.push(each);
      } else if (each.card_type === 'customer_photo') {
        photosList.push(each);
      }
    });

    // Layout: Product | Customer Photo, then Review, repeat
    let productIndex = 0;
    let photoIndex = 0;
    let reviewIndex = 0;
    let itemIndex = 0;

    while (
      productsList[productIndex] ||
      photosList[photoIndex] ||
      reviewsList[reviewIndex]
    ) {
      // Create row: Product | Customer Photo
      if (productsList[productIndex] || photosList[photoIndex]) {
        const productCard = productsList[productIndex];
        const photoCard = photosList[photoIndex];

        // Only create row if we have at least one item
        if (productCard || photoCard) {
          const productData =
            productCard?.card_type === 'product'
              ? allStaticProductData.find(
                  product =>
                    parseInt(product.id, 10) === parseInt(productCard.id, 10)
                )
              : null;

          items.push(
            <div
              className={classNames(
                'grid gap-[10px] max-[767px]:mt-2 md:gap-4 min-[1200px]:grid-cols-[repeat(auto-fit,minmax(300px,33.333%))] max-[767px]:[&>*]:max-w-[100vw]',
                {
                  'xl:grid-cols-[repeat(auto-fit,minmax(45%,1fr))] xl:m-0': isMixedGridTab,
                }
              )}
              key={`grid-pair-${itemIndex}`}
            >
              {productCard && (
                <ErrorTryCatch
                  key={`prod-${itemIndex}-${productCard.id ||
                    productCard.card_type ||
                    itemIndex}`}
                >
                  <MixedGridCard
                    {...productCard}
                    index={productIndex}
                    hits={hits}
                    hoveredValue={hoveredValue}
                    layout={layout}
                    onHoverHandler={onHoverHandler}
                    toggleModalView={toggleModalView}
                    productData={productData}
                  />
                </ErrorTryCatch>
              )}

              {photoCard && (
                <ErrorTryCatch
                  key={`photo-${itemIndex}-${photoCard.id ||
                    photoCard.card_type ||
                    itemIndex}`}
                >
                  <MixedGridCard
                    {...photoCard}
                    index={photoIndex}
                    hits={hits}
                    hoveredValue={hoveredValue}
                    layout={layout}
                    onHoverHandler={onHoverHandler}
                    toggleModalView={toggleModalView}
                  />
                </ErrorTryCatch>
              )}
            </div>
          );

          if (productCard) productIndex++;
          if (photoCard) photoIndex++;
          itemIndex++;
        }
      }

      // Add review row
      if (reviewsList[reviewIndex]) {
        const reviewCard = reviewsList[reviewIndex++];
        items.push(
          <div className="flex flex-col" key={`review-container-${itemIndex}`}>
            <ErrorTryCatch
              key={`review-${itemIndex}-${reviewCard.id ||
                reviewCard.card_type ||
                itemIndex}`}
            >
              <MixedGridCard
                {...reviewCard}
                index={reviewIndex - 1}
                hits={hits}
                hoveredValue={hoveredValue}
                layout={layout}
                onHoverHandler={onHoverHandler}
                toggleModalView={toggleModalView}
              />
            </ErrorTryCatch>
          </div>
        );
        itemIndex++;
      }
    }
    return items;
  }, [
    allStaticProductData,
    data,
    hits,
    hoveredValue,
    isMixedGridTab,
    layout,
    onHoverHandler,
    toggleModalView,
  ]);

  return (
    <>
      {gridItems} {loadingMore ? <MixedGridCardPlaceholder /> : null}
    </>
  );
};

MixedGridItemsWithReviews.propTypes = {
  allStaticProductData: PropTypes.array,
  data: PropTypes.array,
  from: PropTypes.string,
  gridWithOneCol: PropTypes.bool,
  gridWithReviews: PropTypes.bool,
  hits: PropTypes.number,
  hoveredValue: PropTypes.object,
  isMixedGridTab: PropTypes.bool,
  keyId: PropTypes.string,
  layout: PropTypes.string,
  loading: PropTypes.bool,
  loadMore: PropTypes.func,
  loadingMore: PropTypes.bool,
  maxCardsCount: PropTypes.number,
  onHoverHandler: PropTypes.func,
  toggleModalView: PropTypes.func,
};

export default MixedGridItemsWithReviews;
